import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HeadToHeadQueryContext } from '../head-to-head-query-context';
import { GermplasmPairModel } from '../model/germplasm-pair.model';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../../shared/alert/alert.service';
import { ColumnHeadersEnum } from '../model/column-header.enum';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { JhiEventManager } from '../../shared/core-utils';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { GermplasmSearchRequest } from '../../entities/germplasm/germplasm-search-request.model';
import { Germplasm } from '../../entities/germplasm/germplasm.model';
import { SORT_PREDICATE_NONE } from '../../germplasm-manager/germplasm-search-resolve-paging-params';
import { VIEW_NAMES_PERMISSIONS } from '../../shared/auth/permissions';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';

@Component({
    selector: 'jhi-specify-entries',
    styleUrl: './specify-entries-step.component.scss',
    templateUrl: './specify-entries-step.component.html'
})
export class SpecifyEntriesStepComponent implements OnInit, OnDestroy {
    @Output() next = new EventEmitter<void>();
    @Input() active = false;

    germplasmPairs: GermplasmPairModel[] = [];
    testEntryGIDS: Set<number> = new Set();
    standardEntryGIDS: Set<number> = new Set();
    germplasmMap: Map<number, Germplasm> = new Map<number, Germplasm>();
    selectedItems: Map<number, GermplasmPairModel> = new Map<number, GermplasmPairModel>();
    lastClickIndex: any;

    // Pagination properties
    itemsPerPage: number = 20;
    page: number;
    predicate: any;
    isAscending: boolean;
    isLoading: boolean;
    totalItems = 0;
    previousPage: any;
    pagedData: GermplasmPairModel[] = [];
    isSelectAll: boolean;

    ColumnHeaders = ColumnHeadersEnum;
    VIEW_NAMES_PERMISSIONS = [...VIEW_NAMES_PERMISSIONS];
    private subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    private currentPopover: NgbPopover;

    constructor(public queryContext: HeadToHeadQueryContext,
                public alertService: AlertService,
                private eventManager: JhiEventManager,
                private germplasmService: GermplasmService,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService) {
    }

    ngOnInit(): void {
        this.page = 1;
        this.predicate = SORT_PREDICATE_NONE;
        this.subscriptionsManager.add = this.eventManager.subscribe('addGermplasmToEntries', (event) => {
            this.addGermplasmToEntries(event.content);
        });
        this.subscriptionsManager.add = this.eventManager.subscribe('addListToEntries', (event) => {
            this.addListToEntries(event.content);
        });
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    addGermplasmToEntries(content: any) : void {
        const request = new GermplasmSearchRequest();
        request.addedColumnsPropertyIds = ['PREFERRED NAME'];
        request.gids = content.selectedGids;
        this.addToEntries(content, request);
    }

    addListToEntries(content: any) : void {
        const request = new GermplasmSearchRequest();
        request.germplasmListIds = [content.listId];
        request.addedColumnsPropertyIds = ['PREFERRED NAME'];
        this.addToEntries(content, request);
    }

    addToEntries(content: any, request: GermplasmSearchRequest) {
        this.isLoading = true;
        this.subscriptionsManager.add = this.germplasmService.search(request).subscribe((searchRequestId) => {
            this.germplasmService.getAllGermplasm(searchRequestId).toPromise().then((filteredGermplasm) => {
                filteredGermplasm.forEach((germplasm) => {
                    this.germplasmMap.set(germplasm.gid, germplasm);
                    if (content.isForTestEntries) {
                        if (this.testEntryGIDS.size === 0 && this.standardEntryGIDS.size > 0) {
                            this.germplasmPairs = [];
                        }
                        const testEntry = new GermplasmPairModel();
                        testEntry.testGID = germplasm.gid;
                        testEntry.testDesignation = germplasm.germplasmPreferredName;
                        testEntry.testGroupId = germplasm.groupId;
                        if (this.standardEntryGIDS.size > 0) {
                            this.standardEntryGIDS.forEach((standardGid) => {
                                if (standardGid !== germplasm.gid) {
                                    const entry = { ...testEntry };
                                    const standardGermplasm = this.germplasmMap.get(standardGid);
                                    entry.standardGID = standardGid;
                                    entry.standardDesignation = standardGermplasm.germplasmPreferredName;
                                    entry.standardGroupId = standardGermplasm.groupId;
                                    entry.entryNumber = this.germplasmPairs.length + 1;
                                    this.germplasmPairs.push(entry);
                                    this.testEntryGIDS.add(germplasm.gid);
                                }
                            });
                            if (this.testEntryGIDS.size === 0) {
                                this.clearAll();
                            }
                        } else {
                            testEntry.entryNumber = this.germplasmPairs.length + 1;
                            this.germplasmPairs.push(testEntry);
                            this.testEntryGIDS.add(germplasm.gid);
                        }
                    } else {
                        if (this.standardEntryGIDS.size === 0 && this.testEntryGIDS.size > 0) {
                            this.germplasmPairs = [];
                        }
                        const standardEntry = new GermplasmPairModel();
                        standardEntry.entryNumber = this.germplasmPairs.length + 1;
                        standardEntry.standardGID = germplasm.gid;
                        standardEntry.standardDesignation = germplasm.germplasmPreferredName;
                        standardEntry.standardGroupId = germplasm.groupId;
                        if (this.testEntryGIDS.size > 0) {
                            this.testEntryGIDS.forEach((testGID) => {
                                if (testGID !== germplasm.gid) {
                                    const entry = { ...standardEntry };
                                    const testGermplasm = this.germplasmMap.get(testGID);
                                    entry.testGID = testGID;
                                    entry.testDesignation = testGermplasm.germplasmPreferredName;
                                    entry.testGroupId = testGermplasm.groupId;
                                    entry.entryNumber = this.germplasmPairs.length + 1;
                                    this.standardEntryGIDS.add(germplasm.gid);
                                    this.germplasmPairs.push(entry);
                                }
                            });
                            if (this.standardEntryGIDS.size === 0) {
                                this.clearAll();
                            }
                        } else {
                            standardEntry.entryNumber = this.germplasmPairs.length + 1;
                            this.germplasmPairs.push(standardEntry);
                            this.standardEntryGIDS.add(germplasm.gid);
                        }
                    }
                });
                this.updatePagedData();
            });
        });
    }

    nextStep(): void {
        if (!this.disableNextStep()) {
            this.queryContext.germplasmPairs = this.germplasmPairs;
            this.queryContext.standardGIDs = this.standardEntryGIDS;
            this.queryContext.testEntryGIDS = this.testEntryGIDS;
            this.next.emit();
        }
    }

    disableNextStep(): boolean {
        return this.testEntryGIDS.size === 0 || this.standardEntryGIDS.size === 0;
    }

    trackId(index: number, item: GermplasmPairModel) {
        return item.entryNumber;
    }

    sort() {
        if (this.predicate === this.ColumnHeaders.TEST_GID) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.testGID) > Number(b.testGID) ? 1 : -1;
                } else {
                    return Number(a.testGID) < Number(b.testGID) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.TEST_GROUP_ID) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.testGroupId) > Number(b.testGroupId) ? 1 : -1;
                } else {
                    return Number(a.testGroupId) < Number(b.testGroupId) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.TEST_DESIG) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return a.testDesignation > b.testDesignation ? 1 : -1;
                } else {
                    return a.testDesignation < b.testDesignation ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.STANDARD_GID) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.standardGID) > Number(b.standardGID) ? 1 : -1;
                } else {
                    return Number(a.standardGID) < Number(b.standardGID) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.STANDARD_GROUP_ID) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.standardGroupId) > Number(b.standardGroupId) ? 1 : -1;
                } else {
                    return Number(a.standardGroupId) < Number(b.standardGroupId) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.STANDARD_DESIG) {
            this.germplasmPairs.sort((a, b) => {
                if (this.isAscending) {
                    return a.standardDesignation > b.standardDesignation ? 1 : -1;
                } else {
                    return a.standardDesignation < b.standardDesignation ? 1 : -1;
                }
            });
        }
        this.updatePagedData();
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    toggleSelect($event, index, selectedEntriesModel: GermplasmPairModel, checkbox = false) {
        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.germplasmPairs.slice(min, max);
        } else {
            items = [selectedEntriesModel];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(selectedEntriesModel.entryNumber);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.entryNumber);
            } else {
                this.selectedItems.set(item.entryNumber, item);
            }
        }
    }

    isPageSelected() {
        return this.pagedData.length && this.pagedData.every((entry: GermplasmPairModel) => Boolean(this.selectedItems.get(entry.entryNumber)));
    }

    isSelected(entriesModel: GermplasmPairModel) {
        return this.selectedItems.get(entriesModel.entryNumber);
    }

    showRightClickMenu($event, p: NgbPopover) {
        if (this.currentPopover) {
            this.currentPopover.close();
        }
        if (this.germplasmPairs && this.germplasmPairs.length > 0) {
            $event.preventDefault();
            this.currentPopover = p;
            p.open();
        }
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            this.pagedData.forEach((entry: GermplasmPairModel) => this.selectedItems.delete(entry.entryNumber));
        } else {
            this.pagedData.forEach((entry: GermplasmPairModel) => this.selectedItems.set(entry.entryNumber, entry));
        }
    }

    updatePagedData(): void {
        this.isLoading = false;
        if (this.germplasmPairs && this.germplasmPairs.length > 0) {
            this.totalItems = this.germplasmPairs.length;

            // Calculate starting and ending indices for the current page
            const startIndex = (this.page - 1) * this.itemsPerPage;
            const endIndex = Math.min(Number(startIndex) + Number(this.itemsPerPage), this.totalItems);
            // Slice the data to get the current page data
            this.pagedData = this.germplasmPairs.slice(startIndex, endIndex);
        } else {
            this.pagedData = [];
            this.totalItems = 0;
        }
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.updatePagedData();
        }
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.updatePagedData();
    }

    removeSelected() {
        if (this.selectedItems.size === 0) {
            this.alertService.warning('design-crosses.selection.empty');
            return;
        }
        this.standardEntryGIDS.clear();
        this.testEntryGIDS.clear();
        if (this.isSelectAll) {
            this.germplasmPairs = [];
            this.germplasmMap.clear();
        } else {
            this.germplasmPairs = this.germplasmPairs.filter((cross) => !this.selectedItems.get(cross.entryNumber));
            this.germplasmPairs.forEach((entry) => {
                if (entry.standardGID) {
                    this.standardEntryGIDS.add(entry.standardGID);
                }
                if (entry.testGID) {
                    this.testEntryGIDS.add(entry.testGID);
                }
            });
        }
        this.selectedItems.clear();

        this.germplasmPairs.forEach((cross, index) => {
            cross.entryNumber = index + 1;
        });

        this.germplasmPairs.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
        this.totalItems = this.germplasmPairs.length;
        this.updatePagedData();
    }

    clearAll() {
        this.germplasmPairs = [];
        this.testEntryGIDS.clear();
        this.standardEntryGIDS.clear();
        this.selectedItems.clear();
        this.totalItems = 0;
    }

    selectAll() {
        this.germplasmPairs.forEach((entry: GermplasmPairModel) => this.selectedItems.set(entry.entryNumber, entry));
    }

    onSelectAll(isSelectAll) {
        this.isSelectAll = !isSelectAll;
        this.clearSelectedItems();
        if (this.isSelectAll) {
            this.selectAll();
        }
    }

    onClearSort($event) {
        $event.preventDefault();
        this.germplasmPairs.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
        this.updatePagedData();
    }

    protected readonly Math = Math;
    protected readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
}
